import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  TagIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { bourbonApi, BourbonListing, BourbonSearchParams } from '../services/bourbonApi'
import CreateBourbonListingModal from '../components/CreateBourbonListingModal'
import ViewBourbonListingModal from '../components/ViewBourbonListingModal'
import EditBourbonListingModal from '../components/EditBourbonListingModal'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

export default function BourbonSwapPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedListing, setSelectedListing] = useState<BourbonListing | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    type: '' as 'have' | 'want' | '',
    category: '',
    brand: '',
    location: '',
    status: 'available'
  })
  const [allListings, setAllListings] = useState<BourbonListing[]>([])
  const [nextKey, setNextKey] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Handle direct links to specific listings
  useEffect(() => {
    const listingId = searchParams.get('id')
    if (listingId && allListings.length > 0) {
      const listing = allListings.find(l => l.id === listingId)
      if (listing) {
        handleViewListing(listing)
        // Remove the id parameter from URL after opening
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev)
          newParams.delete('id')
          return newParams
        })
      }
    }
  }, [allListings, searchParams, setSearchParams])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchInput])

  // Create search parameters from current filters
  const bourbonSearchParams: BourbonSearchParams = {
    limit: 50,
    ...(searchTerm && { search: searchTerm }),
    ...(selectedFilters.type && { type: selectedFilters.type }),
    ...(selectedFilters.category && { category: selectedFilters.category }),
    ...(selectedFilters.brand && { brand: selectedFilters.brand }),
    ...(selectedFilters.location && { location: selectedFilters.location }),
    ...(selectedFilters.status && { status: selectedFilters.status })
  }

  // Check if any filters are active
  const hasActiveFilters = searchInput || 
    selectedFilters.type || 
    selectedFilters.category || 
    selectedFilters.brand || 
    selectedFilters.location ||
    selectedFilters.status !== 'available'

  // Fetch bourbon listings from API
  const { data: bourbonData, isLoading, error, refetch } = useQuery({
    queryKey: ['bourbon-listings', bourbonSearchParams],
    queryFn: () => bourbonApi.getListings(getAccessTokenSilently, bourbonSearchParams),
    keepPreviousData: true,
    staleTime: 30000, // Keep data fresh for 30 seconds
  })

  // Update state when data loads
  useEffect(() => {
    if (bourbonData) {
      setAllListings(bourbonData.items)
      setNextKey(bourbonData.nextKey)
      setHasMore(bourbonData.hasMore)
    }
  }, [bourbonData])

  // Load more listings
  const loadMoreListings = async () => {
    if (!nextKey || isLoadingMore || hasActiveFilters) return

    setIsLoadingMore(true)
    try {
      const moreData = await bourbonApi.getListings(getAccessTokenSilently, { 
        ...bourbonSearchParams,
        lastKey: nextKey 
      })
      
      setAllListings(prev => [...prev, ...moreData.items])
      setNextKey(moreData.nextKey)
      setHasMore(moreData.hasMore)
    } catch (error) {
      console.error('Load more error:', error)
      toast.error('Failed to load more listings')
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchInput('')
    setSearchTerm('')
    setSelectedFilters({
      type: '',
      category: '',
      brand: '',
      location: '',
      status: 'available'
    })
    setShowFilters(false)
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleCreateSuccess = () => {
    setShowCreateModal(false)
    // Refresh the listings
    refetch()
  }

  const handleViewListing = (listing: BourbonListing) => {
    setSelectedListing(listing)
    setShowViewModal(true)
  }

  const handleEditListing = (listing: BourbonListing) => {
    setSelectedListing(listing)
    setShowViewModal(false)
    setShowEditModal(true)
  }

  const handleEditSuccess = () => {
    setShowEditModal(false)
    setSelectedListing(null)
    // Refresh the listings
    refetch()
    toast.success('Listing updated successfully!')
  }

  const handleDeleteListing = async (listingId: string) => {
    try {
      await bourbonApi.deleteListing(listingId, getAccessTokenSilently)
      
      // Remove from local state
      setAllListings(prev => prev.filter(listing => listing.id !== listingId))
      
      // Close modal if this was the viewed listing
      if (selectedListing?.id === listingId) {
        setShowViewModal(false)
        setSelectedListing(null)
      }
      
      toast.success('Listing deleted successfully')
    } catch (error) {
      console.error('Error deleting listing:', error)
      toast.error('Failed to delete listing')
      throw error
    }
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'completed': return 'text-blue-600 bg-blue-100'
      case 'expired': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  // Get type color
  const getTypeColor = (type: string) => {
    return type === 'have' 
      ? 'text-green-700 bg-green-100' 
      : 'text-blue-700 bg-blue-100'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-rb-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rb-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              🥃 Bourbon Swap
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Connect with fellow Railbirds to trade, buy, or find that special bottle. Share what you have or request what you're seeking.
            </p>
          </div>

          {/* Search and Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search bourbon, brands, descriptions..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                    hasActiveFilters || showFilters
                      ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <FunnelIcon className="w-4 h-4 mr-2" />
                  Filters
                  {hasActiveFilters && (
                    <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                      Active
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  New Listing
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      value={selectedFilters.type}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, type: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Types</option>
                      <option value="have">Have (Offering)</option>
                      <option value="want">Want (Seeking)</option>
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      value={selectedFilters.category}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Categories</option>
                      <option value="bourbon">Bourbon</option>
                      <option value="whiskey">Whiskey</option>
                      <option value="rye">Rye</option>
                      <option value="scotch">Scotch</option>
                      <option value="irish">Irish</option>
                      <option value="japanese">Japanese</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Brand Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Buffalo Trace"
                      value={selectedFilters.brand}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, brand: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      placeholder="City, State"
                      value={selectedFilters.location}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={selectedFilters.status}
                      onChange={(e) => setSelectedFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="available">Available</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4 mr-2" />
                      Clear All Filters
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Results Summary */}
          {bourbonData && (
            <div className="text-center text-white/80">
              <p>
                Showing {allListings.length} of {bourbonData.count} listings
                {hasActiveFilters && ' (filtered)'}
              </p>
            </div>
          )}

          {/* Listings Grid */}
          {allListings.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {allListings.map((listing) => (
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => handleViewListing(listing)}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700 cursor-pointer"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(listing.type)}`}>
                          {listing.type === 'have' ? 'OFFERING' : 'SEEKING'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(listing.status)}`}>
                          {listing.status.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
                        {listing.title}
                      </h3>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-3 mb-4">
                    {listing.brand && (
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <TagIcon className="w-4 h-4 mr-2" />
                        <span>{listing.brand}</span>
                        {listing.age && (
                          <span className="ml-2">
                            • {typeof listing.age === 'number' ? `${listing.age}yr` : listing.age}
                          </span>
                        )}
                        {listing.proof && <span className="ml-2">• {listing.proof}°</span>}
                      </div>
                    )}

                    {listing.location && (
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <MapPinIcon className="w-4 h-4 mr-2" />
                        <span>{listing.location}</span>
                      </div>
                    )}

                    {listing.description && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                        {listing.description}
                      </p>
                    )}

                    {listing.tradePreferences && (
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        <span className="font-medium">Trade preferences: </span>
                        <span className="line-clamp-2">{listing.tradePreferences}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <UserIcon className="w-4 h-4 mr-1" />
                      <span>{listing.userName}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      <span>{formatDate(listing.createdAt)}</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  {listing.userId !== user?.sub && listing.status === 'available' && (
                    <div className="mt-4">
                      <button className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                        Contact Member
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🥃</div>
              <h3 className="text-xl font-semibold text-white mb-2">No listings found</h3>
              <p className="text-white/80 mb-6">
                {hasActiveFilters 
                  ? 'Try adjusting your filters or search terms.' 
                  : 'Be the first to create a bourbon listing!'
                }
              </p>
              {!hasActiveFilters && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create First Listing
                </button>
              )}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !hasActiveFilters && allListings.length > 0 && (
            <div className="text-center">
              <button
                onClick={loadMoreListings}
                disabled={isLoadingMore}
                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoadingMore ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More Listings
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Create Listing Modal */}
      <CreateBourbonListingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* View Listing Modal */}
      <ViewBourbonListingModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false)
          setSelectedListing(null)
        }}
        listing={selectedListing}
        onDelete={handleDeleteListing}
        onEdit={handleEditListing}
      />

      {/* Edit Listing Modal */}
      <EditBourbonListingModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedListing(null)
        }}
        listing={selectedListing}
        onSuccess={handleEditSuccess}
      />
    </div>
  )
}
